import type { FieldQuestion } from './field';
import type { NameQuestion } from './names';
import type { PhotoQuestion } from './photo';
import type { SoundQuestion } from './sound';
import type { SoundMatchQuestion } from './sound-match';

// A missed question, dealt again at the end of the round — see `retryMisses` in
// ./settings.ts. The same bird, the same picture, the same recording: the point
// is to meet the thing that was missed. Only the order of the answers changes,
// so the second attempt can't be won by remembering where the right one sat.

/** A shuffled copy of `0..n-1`. */
function order(n: number, rnd: () => number): number[] {
  const out = Array.from({ length: n }, (_, i) => i);
  for (let i = n - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

export function reaskName(q: NameQuestion, rnd: () => number = Math.random): NameQuestion {
  const o = order(q.choices.length, rnd);
  return { ...q, choices: o.map((i) => q.choices[i]) };
}

export function reaskSound(q: SoundQuestion, rnd: () => number = Math.random): SoundQuestion {
  const o = order(q.choices.length, rnd);
  return { ...q, choices: o.map((i) => q.choices[i]) };
}

/** `photos` and `mirrored` are parallel to `choices`, so all three move together. */
export function reaskPhoto(q: PhotoQuestion, rnd: () => number = Math.random): PhotoQuestion {
  const o = order(q.choices.length, rnd);
  return {
    ...q,
    choices: o.map((i) => q.choices[i]),
    photos: o.map((i) => q.photos[i]),
    mirrored: o.map((i) => q.mirrored[i]),
  };
}

export function reaskMatch(
  q: SoundMatchQuestion,
  rnd: () => number = Math.random,
): SoundMatchQuestion {
  const o = order(q.clips.length, rnd);
  return {
    ...q,
    clips: o.map((i) => q.clips[i]),
    codes: o.map((i) => q.codes[i]),
    answer: o.indexOf(q.answer),
  };
}

export function reaskField(q: FieldQuestion, rnd: () => number = Math.random): FieldQuestion {
  if (q.skill === 'sound') return { skill: q.skill, question: reaskSound(q.question, rnd) };
  if (q.skill === 'photo') return { skill: q.skill, question: reaskPhoto(q.question, rnd) };
  return { skill: q.skill, question: reaskName(q.question, rnd) };
}
